import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { FuelCurveGraph } from "../fuel-curve-graph/fuel-curve-graph";
import { fuelViewColors, useFuelStatistics } from "hooks/use-fuel-statistics";
import { useStore } from "hooks/use-store";
import { PLACEHOLDER } from "utils/constants";
import { Color } from "utils/theme";
import { RecommendedSogType } from "types/enums";

export const FuelCurveView = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "fuel_curve",
  });
  const sog = useStore((state) => state.sog);

  const {
    displayFuelRateNmi,
    hasRecommendedSog,
    recommendedSogType,
    recommendedSog,
    isSogUnsafe,
    isFuelRateBad,
    safetySpeedLabel,
  } = useFuelStatistics();

  return (
    <section className="h-full flex flex-col w-full space-y-2 font-medium pb-4">
      <FuelCurveGraph />
      <div className="flex space-x-2">
        <FuelCurveViewTile>
          <p
            className="text-7xl font-mono font-semibold"
            style={{
              color: isFuelRateBad ? Color.Rose[600] : Color.White,
            }}
          >
            {displayFuelRateNmi}
          </p>
          <p className="text-2xl" style={{ color: Color.Primary[400] }}>
            {t("units.liters_per_nm")}
          </p>
        </FuelCurveViewTile>
        <FuelCurveViewTile>
          <p
            className="text-7xl font-mono font-semibold"
            style={{ color: isSogUnsafe ? Color.Rose[600] : Color.White }}
          >
            {sog !== null ? Math.round(sog) : PLACEHOLDER}
          </p>
          <p className="text-2xl" style={{ color: Color.Primary[400] }}>
            {t("sog")}
          </p>
        </FuelCurveViewTile>
        {hasRecommendedSog ? (
          <FuelCurveViewTile>
            <p
              className="text-7xl font-mono font-semibold"
              style={{ color: fuelViewColors.Good }}
            >
              {recommendedSog}
            </p>
            <p className="text-2xl" style={{ color: Color.Primary[400] }}>
              {recommendedSogType === RecommendedSogType.Eco
                ? t("eco_speed")
                : safetySpeedLabel}
            </p>
          </FuelCurveViewTile>
        ) : null}
      </div>
    </section>
  );
};

interface FuelCurveViewTileProps extends React.HTMLAttributes<HTMLDivElement> {
  backgroundColor?: string;
  children: React.ReactNode;
}

const FuelCurveViewTile = ({
  backgroundColor,
  children,
  ...props
}: FuelCurveViewTileProps) => {
  return (
    <div
      className={classNames(
        "p-6 flex-1 flex flex-col items-center justify-center text-center space-y-3",
        props.className
      )}
      style={{
        background: backgroundColor || Color.Primary[900],
      }}
    >
      {children}
    </div>
  );
};
